import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { SUPPLIER_TRADE_LABELS, nzDate, parseRef } from '@promanage/core';
import {
  MAINTENANCE_URGENCY_LABELS,
  maintenanceResponseOverdue,
} from '@promanage/core/maintenance';
import { Button, Amount } from '@promanage/ui';
import { ColumnPicker } from '../features/table/ColumnPicker';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type SortState } from '../features/table/columns';
import { useColumnState } from '../features/table/useColumnState';
import {
  DEFAULT_MAINTENANCE_SORT,
  MAINTENANCE_COLUMNS,
  type MaintenanceColumnId,
} from '../features/maintenance/maintenanceColumns';
import type { MaintenanceJobRow } from '../features/maintenance/types';
import { trpc } from '../lib/trpc';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import { workspaceChoice, workspaceTable, workspaceText } from '../features/workspaces/state';
import { usePortfolioScope } from '../features/portfolio/scope';
import { MaintenanceNav } from '../features/maintenance/MaintenanceNav';

export const Route = createFileRoute('/maintenance/')({
  component: MaintenancePage,
});

const STORAGE_KEY = 'promanage:maintenance-columns';

/** One identity, so the pending state does not re-sort the table on every render. */
const NO_JOBS: MaintenanceJobRow[] = [];

type JobFilter = 'open' | 'unactioned' | 'overdue' | 'awaiting_order' | 'in_progress' | 'all';

const FILTERS: { value: JobFilter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'unactioned', label: 'Nobody sent' },
  { value: 'overdue', label: 'Past target' },
  { value: 'awaiting_order', label: 'Approved, not ordered' },
  { value: 'in_progress', label: 'With a contractor' },
  { value: 'all', label: 'All' },
];

/**
 * Maintenance — what is broken, and what the agency is doing about it.
 *
 * THE DEFAULT FILTER IS "OPEN", not "All", and that is the difference between a queue and an
 * archive. Every job an agency has ever fixed stays on this table for ever; what somebody sitting
 * down at it on a Tuesday morning needs is the handful nobody has dealt with.
 *
 * THE ORDER IS A TRIAGE DECISION rather than a filing one — worst first, then oldest, on both the
 * server and here. Every other list in this console starts at newest-first, and this one does not,
 * because an emergency reported this morning outranks a routine job from last week.
 *
 * THE SUBTITLE COUNTS OBLIGATIONS, not throughput. "Nobody sent" is work the agency has been told
 * about and has not acted on, which is the number a Tenancy Tribunal application is built out of;
 * "with a contractor" is money that is committed and not yet invoiced. Neither is a total of jobs
 * completed, because nobody has ever needed that at 9am.
 *
 * "PAST TARGET" IS THE AGENCY'S OWN SERVICE TARGET AND THE PAGE SAYS SO. The RTA requires a
 * landlord to act within a reasonable time and puts no number of days on it; a screen that
 * presented these as legal deadlines would be making a compliance claim this product has not
 * verified (CLAUDE.md, open questions).
 */
function MaintenancePage() {
  const navigate = useNavigate();
  const { scope, setScope, canToggle } = usePortfolioScope();
  const list = useQuery(
    trpc.maintenance.list.queryOptions(
      { scope },
      { meta: { errorTitle: 'Could not load the maintenance queue' }, staleTime: 10_000 },
    ),
  );
  const summary = useQuery(trpc.maintenance.summary.queryOptions({ scope }, { staleTime: 10_000 }));
  const jobs = list.data ?? NO_JOBS;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<JobFilter>('open');
  const [sort, setSort] = useState<SortState<MaintenanceColumnId>>(DEFAULT_MAINTENANCE_SORT);
  const columns = useColumnState(STORAGE_KEY, MAINTENANCE_COLUMNS);
  const workspace = useWorkspace({
    route: '/maintenance',
    state: { filters: { query, filter, scope }, columns: columns.state.order, sort },
    apply(state) {
      const table = workspaceTable(state, MAINTENANCE_COLUMNS, DEFAULT_MAINTENANCE_SORT);
      setQuery(workspaceText(state, 'query'));
      setFilter(
        workspaceChoice(
          state,
          'filter',
          FILTERS.map((option) => option.value),
          'open',
        ),
      );
      if (canToggle) setScope(workspaceChoice(state, 'scope', ['own', 'agency'], 'own'));
      columns.setState(table.columns);
      setSort(table.sort);
    },
  });

  const today = nzDate(new Date());

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const open = (job: MaintenanceJobRow) => job.state !== 'completed' && job.state !== 'cancelled';

    return jobs
      .filter((job) => {
        if (filter === 'all') return true;
        if (filter === 'unactioned') return open(job) && job.orderedOn === null;
        if (filter === 'overdue') {
          return open(job) && maintenanceResponseOverdue(job.responseDueOn, job.orderedOn, today);
        }
        if (filter === 'awaiting_order') return job.state === 'approved';
        if (filter === 'in_progress') return job.state === 'in_progress';
        return open(job);
      })
      .filter((job) => {
        if (!q) return true;

        const asRef = parseRef(q);
        if (asRef) {
          if (asRef.entity === 'maintenance') return asRef.sequence === job.refSeq;
          // A property or tenancy ref pasted into the box is a reasonable way to ask "everything
          // for this house" without leaving the page.
          if (asRef.entity === 'property') return job.propertyRef.toLowerCase() === q;
          if (asRef.entity === 'tenancy') return (job.tenancyRef ?? '').toLowerCase() === q;
          return false;
        }

        return [
          job.ref,
          job.propertyAddress,
          job.summary,
          job.details,
          SUPPLIER_TRADE_LABELS[job.trade],
          MAINTENANCE_URGENCY_LABELS[job.urgency],
          job.reportedByPersonName,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => compareRows(a, b, sort, MAINTENANCE_COLUMNS));
  }, [jobs, query, filter, sort, today]);

  const total = jobs.length;
  const counted = list.isPending
    ? 'Loading…'
    : rows.length === total
      ? `${total} ${total === 1 ? 'job' : 'jobs'}`
      : `Showing ${rows.length} of ${total} jobs`;

  const obligations = summary.data ? (
    <>
      {counted} · {String(summary.data.awaitingResponse)} urgent with nobody sent ·{' '}
      <Amount value={BigInt(summary.data.committedCents)} currency tone="neutral" /> out with
      contractors
    </>
  ) : (
    counted
  );

  return (
    <ListShell
      nav={<MaintenanceNav />}
      title="Maintenance"
      subtitle={list.isPending ? counted : obligations}
      actions={
        <>
          <WorkspaceMenu workspace={workspace} />
          <ColumnPicker
            columns={MAINTENANCE_COLUMNS}
            state={columns.state}
            onChange={columns.setState}
            onReset={columns.reset}
          />
          <Link to="/maintenance/new">
            <Button size="sm">
              <span aria-hidden="true">+</span> Report a job
            </Button>
          </Link>
        </>
      }
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Search address, fault, trade or ref"
      searchLabel="Search maintenance jobs"
      filters={
        <FilterChips label="Filter jobs" value={filter} options={FILTERS} onChange={setFilter} />
      }
      isPending={list.isPending}
      error={list.error}
      onRetry={() => void list.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState
          title={query ? `Nothing matches “${query}”` : 'Nothing outstanding'}
          action={
            <Link to="/maintenance/new">
              <Button size="sm">Report a job</Button>
            </Link>
          }
        >
          {query
            ? 'Try a different address, fault, trade or ref.'
            : 'A job is one piece of work on one property. Recording it commits nothing — approving a figure is what authorises the spend, and the contractor’s invoice is entered as a bill against the job afterwards.'}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={columns.visible}
        sort={sort}
        onSortChange={setSort}
        rowKey={(job) => job.id}
        onOpen={(job) => void navigate({ to: '/maintenance/$ref', params: { ref: job.ref } })}
        caption={`Maintenance jobs, worst first. ${counted}. Respond-by dates are the agency's own service target, not a statutory deadline.`}
        minWidth="66rem"
      />
    </ListShell>
  );
}
